import { CODES, EVENT, KEYS } from "@excalidraw/common";
import { THEME } from "@excalidraw/excalidraw";
import { useEffect, useLayoutEffect, useState } from "react";

import type { Theme } from "@excalidraw/element/types";

import { STORAGE_KEYS } from "./app_constants";
const getDefaultTheme = () => {
  const theme = /iPad|iPhone|iPod/.test(navigator.userAgent)
    ? THEME.LIGHT
    : THEME.DARK;
  //TODO: enforce light theme for safari engine on apple devices
  //https://github.com/excalidraw/excalidraw/issues/5312
  //https://github.com/excalidraw/excalidraw/issues/6516
  return theme;
};
const getDarkThemeMediaQuery = (): MediaQueryList | undefined =>
  window.matchMedia?.("(prefers-color-scheme: dark)");

export const useHandleAppTheme = () => {
  const isAppleDevice = /iPad|iPhone|iPod|Mac/.test(navigator.userAgent);

  const [appTheme, setAppTheme] = useState<Theme | "system">(() => {
    // Force light theme for Apple devices
    if (isAppleDevice) {
      return THEME.LIGHT;
    }
    return (
      (localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE_THEME) as
        | Theme
        | "system"
        | null) || getDefaultTheme()
    );
  });
  const [editorTheme, setEditorTheme] = useState<Theme>(THEME.LIGHT);

  useEffect(() => {
    // Skip theme changes for Apple devices
    if (isAppleDevice) {
      return;
    }

    const mediaQuery = getDarkThemeMediaQuery();

    const handleChange = (e: MediaQueryListEvent) => {
      setEditorTheme(e.matches ? THEME.DARK : THEME.LIGHT);
    };

    if (appTheme === "system") {
      mediaQuery?.addEventListener("change", handleChange);
    }

    const handleKeydown = (event: KeyboardEvent) => {
      if (
        !event[KEYS.CTRL_OR_CMD] &&
        event.altKey &&
        event.shiftKey &&
        event.code === CODES.D
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        // Prevent theme change for Apple devices
        if (!isAppleDevice) {
          setAppTheme(editorTheme === THEME.DARK ? THEME.LIGHT : THEME.DARK);
        }
      }
    };

    document.addEventListener(EVENT.KEYDOWN, handleKeydown, { capture: true });

    return () => {
      mediaQuery?.removeEventListener("change", handleChange);
      document.removeEventListener(EVENT.KEYDOWN, handleKeydown, {
        capture: true,
      });
    };
  }, [appTheme, editorTheme, setAppTheme, isAppleDevice]);

  useLayoutEffect(() => {
    // Always use light theme for Apple devices
    if (isAppleDevice) {
      localStorage.setItem(STORAGE_KEYS.LOCAL_STORAGE_THEME, THEME.LIGHT);
      setEditorTheme(THEME.LIGHT);
      return;
    }

    localStorage.setItem(STORAGE_KEYS.LOCAL_STORAGE_THEME, appTheme);

    if (appTheme === "system") {
      setEditorTheme(
        getDarkThemeMediaQuery()?.matches ? THEME.DARK : THEME.LIGHT,
      );
    } else {
      setEditorTheme(appTheme);
    }
  }, [appTheme, isAppleDevice]);

  return { editorTheme, appTheme, setAppTheme };
};
